/**
 * Shared run rules for BOTH games, exactly as the canonical design doc specifies:
 * a countdown timer, five mistake chances, score, and a progress counter.
 *
 * "Unified mechanics: both games use five mistake chances and a time limit to
 * ensure a consistent player experience." — game-1-2-design.en.md
 *
 * This lives outside either scene so the rule can never drift between them, and
 * so the HUD has a single source of truth to render.
 */

export const MAX_LIVES = 5;
export const DEFAULT_TIME_SEC = 180; // 3:00, matching the reference HUD.

export type SessionPhase = 'idle' | 'briefing' | 'playing' | 'paused' | 'won' | 'lost';

/**
 * One committed answer. Localised at record time, exactly as the approved build
 * does, so a mid-run language switch cannot retro-translate a past judgement
 * into a mix of two vocabularies.
 */
export interface Judgement {
  /** `${itemId}-attempt-${n}` — stable, and unique across re-tries. */
  readonly id: string;
  /** The bare item id, kept so the review can look up its illustration. */
  readonly itemId: string;
  readonly name: string;
  /** The teaching sentence; shown as the review clue. */
  readonly description: string;
  readonly isRecyclable: boolean;
  /** Whether the item must be washed before entering its material stream. */
  readonly needsCleaning?: boolean;
  readonly category: string;
  /** Human-readable category label, already localised. */
  readonly categoryLabel: string;
  readonly choice: {
    readonly isRecyclable: boolean;
    readonly needsCleaning?: boolean;
    readonly category: string;
    readonly categoryLabel: string;
  };
}

export interface SessionSnapshot {
  phase: SessionPhase;
  score: number;
  lives: number;
  collected: number;
  total: number;
  timeLeftSec: number;
  streak: number;
}

type Listener = (s: SessionSnapshot) => void;

export class GameSession {
  phase: SessionPhase = 'idle';
  score = 0;
  lives = MAX_LIVES;
  collected = 0;
  total = 0;
  timeLeftSec = DEFAULT_TIME_SEC;
  streak = 0;

  readonly mistakes: Array<{ item: string; chose: string; correct: string; why: string }> = [];

  /**
   * EVERY judgement the player commits, right and wrong, in play order.
   *
   * `mistakes` above is deliberately kept as-is: it is the "what to remember"
   * recap and only ever holds errors. This list is what the approved learning
   * review reads, and it needs the correct answers too — the review has a
   * "Correct" tab, and a player who never sees what they got right cannot
   * revisit their own reasoning.
   *
   * One row PER JUDGEMENT, not per item: re-trying an item after a wrong answer
   * appends a second row, which is why `id` carries the attempt number. That
   * matches the approved payload's `${itemId}-attempt-${n}` exactly.
   *
   * `status` and `category` are graded INDEPENDENTLY by the review — telling a
   * player "recyclable: right, but the material was wrong" is the teaching
   * point, so both halves are stored rather than one combined verdict.
   */
  readonly judgements: Judgement[] = [];

  private listeners = new Set<Listener>();

  reset(total: number, timeSec = DEFAULT_TIME_SEC): void {
    this.phase = 'briefing';
    this.score = 0;
    this.lives = MAX_LIVES;
    this.collected = 0;
    this.total = total;
    this.timeLeftSec = timeSec;
    this.streak = 0;
    this.mistakes.length = 0;
    this.judgements.length = 0;
    this.emit();
  }

  begin(): void {
    if (this.phase === 'briefing' || this.phase === 'paused') {
      this.phase = 'playing';
      this.emit();
    }
  }

  pause(): void {
    if (this.phase === 'playing') {
      this.phase = 'paused';
      this.emit();
    }
  }

  resume(): void {
    if (this.phase === 'paused') {
      this.phase = 'playing';
      this.emit();
    }
  }

  /** Advance the countdown. Only ticks while actually playing. */
  tick(dt: number): void {
    if (this.phase !== 'playing') return;
    this.timeLeftSec = Math.max(0, this.timeLeftSec - dt);
    if (this.timeLeftSec <= 0) this.fail();
    else this.emit();
  }

  /**
   * Register a correct answer. Score rewards streaks so a confident run feels
   * different from a lucky one, and rewards remaining time at the end.
   */
  correct(basePoints = 100): number {
    this.streak++;
    const bonus = Math.min(this.streak - 1, 5) * 20;
    const points = basePoints + bonus;
    this.score += points;
    this.collected++;
    if (this.collected >= this.total) this.win();
    else this.emit();
    return points;
  }

  /**
   * Log one committed answer for the learning review. Called for RIGHT and
   * WRONG alike, before `correct()`/`wrong()` book the score, so the review
   * survives the run ending on this very judgement.
   */
  record(j: Omit<Judgement, 'id'>): void {
    const priors = this.judgements.filter((p) => p.itemId === j.itemId).length;
    this.judgements.push({ ...j, id: `${j.itemId}-attempt-${priors + 1}` });
  }

  /**
   * Accuracy over committed answers, as the approved build computes it:
   * correct judgements / total judgements, rounded, 0 when nothing was tried.
   * Note this is judgement-based, NOT items-found — a player who fixes a
   * mistake on the second try is not credited as if they had been right first.
   */
  accuracyPct(): number {
    const total = this.judgements.length;
    if (total === 0) return 0;
    const right = this.judgements.filter(
      (j) => j.isRecyclable === j.choice.isRecyclable &&
        Boolean(j.needsCleaning) === Boolean(j.choice.needsCleaning) &&
        j.category === j.choice.category
    ).length;
    return Math.round((right / total) * 100);
  }

  /** Register a wrong answer, recording WHY for the teaching panel. */
  wrong(record?: { item: string; chose: string; correct: string; why: string }): void {
    this.streak = 0;
    this.lives--;
    if (record) this.mistakes.push(record);
    if (this.lives <= 0) this.fail();
    else this.emit();
  }

  private win(): void {
    // Time remaining converts to score — rewards decisive play.
    this.score += Math.round(this.timeLeftSec) * 5;
    this.phase = 'won';
    this.emit();
  }

  private fail(): void {
    this.phase = 'lost';
    this.emit();
  }

  snapshot(): SessionSnapshot {
    return {
      phase: this.phase,
      score: this.score,
      lives: this.lives,
      collected: this.collected,
      total: this.total,
      timeLeftSec: this.timeLeftSec,
      streak: this.streak,
    };
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.snapshot());
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    const s = this.snapshot();
    for (const fn of this.listeners) fn(s);
  }
}

/** mm:ss for the HUD clock. */
export function formatTime(sec: number): string {
  const s = Math.max(0, Math.ceil(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
